import { useEffect, useState } from 'react';
import { decideHit, getHitPreview, type DecisionResult, type Hit } from '../api/endpoints.js';
import { asFailure, useAction } from '../api/hooks.js';
import type { ApiFailure } from '../api/client.js';
import {
  answerLabels,
  cardBadge,
  NOT_SURE_NOTE,
  PREVIEW_COPY,
  previewNote,
  shouldLoadCrop,
} from '../flow/hits.js';
import { severityCopy } from '../flow/protection.js';
import { Button, Failure, Raw, Row } from '../ui/kit.js';

/**
 * ONE HIT'S ASK-CARD — "is this your photo?", with the blurred crop.
 *
 * The subject is the deciding human for their own likeness now; staff never see
 * hit imagery at all. Four rules bind this component, and each is somewhere
 * specific in it rather than in a comment at the top:
 *
 *  1. THE CROP RENDERS ONLY ON THIS CARD, and only while it is open
 *     (`shouldLoadCrop`, tested in flow/hits.test.ts). Never in a list, a
 *     digest, an email or a push (services invariant #23).
 *  2. BLURRED BY DEFAULT. The blur is applied SERVICE-SIDE — these bytes are
 *     already blurred, and this component adds no CSS blur, because a CSS blur
 *     over sharp pixels is one screenshot or one devtools toggle away from
 *     being no blur at all.
 *  3. REVEAL IS AN EXPLICIT PER-ITEM TAP. It is a second fetch, deliberately:
 *     it is a second render on their side, and the un-blurred bytes should not
 *     be sitting in the browser until somebody asks for them.
 *  4. THE OBJECT URL IS ALWAYS REVOKED — on close, on unmount, on reveal
 *     replacing it. The effect's cleanup does it, so it does not depend on the
 *     user clicking anything.
 *
 * The page URL is never rendered, in any state: no hot-link to a hit (services
 * invariant #22). Domain and date only.
 */

interface HitCardProps {
  hit: Hit;
  isOpen: boolean;
  onToggle: () => void;
  /** Refresh the list once a decision lands, so the card's state follows. */
  onDecided: () => void;
}

export function HitCard({ hit, isOpen, onToggle, onDecided }: HitCardProps) {
  const wanted = shouldLoadCrop({
    card: hit.card,
    previewExpected: hit.preview_expected,
    isOpen,
  });
  const [revealed, setRevealed] = useState(false);
  const [cropUrl, setCropUrl] = useState<string | null>(null);
  const [cropError, setCropError] = useState<ApiFailure | null>(null);
  const [cropLoading, setCropLoading] = useState(false);

  // Closing the card forgets the reveal. Re-opening starts blurred again —
  // reveal is per-tap, not a preference we remember for them.
  useEffect(() => {
    if (!isOpen) setRevealed(false);
  }, [isOpen]);

  /**
   * The fetch, and the revoke.
   *
   * The cleanup is what makes "the bytes exist for one render" true in a
   * browser: it fires when the card closes, when the component unmounts, and
   * when `revealed` flips (replacing the blurred URL with the revealed one).
   * `live` guards a response that arrives after the user has already closed
   * the card — those bytes are dropped rather than held in state.
   */
  useEffect(() => {
    if (!wanted) {
      setCropUrl(null);
      setCropError(null);
      return;
    }
    let live = true;
    let created: string | null = null;
    setCropLoading(true);
    setCropError(null);
    getHitPreview(hit.hit_id, revealed).then(
      (blob) => {
        setCropLoading(false);
        if (!live) return;
        created = URL.createObjectURL(blob);
        setCropUrl(created);
      },
      (failure: unknown) => {
        setCropLoading(false);
        if (!live) return;
        setCropUrl(null);
        setCropError(asFailure(failure));
      },
    );
    return () => {
      live = false;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [wanted, hit.hit_id, revealed]);

  const decide = useAction(decideHit, () => {
    onDecided();
  });

  const site = hit.matches[0]?.source_domain ?? null;
  const seen = hit.matches[0]?.detected_at ?? null;

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
      <Row>
        <span className={hit.card === 'ask' ? 'badge warn' : 'badge'}>{cardBadge(hit.card)}</span>
        {/* Domain and date. The page link is below, and only once the api
            serves page_url — i.e. after they marked the hit as abuse. */}
        <strong>{site ?? 'an unnamed site'}</strong>
        <span className="muted">{seen === null ? 'date unknown' : new Date(seen).toLocaleDateString()}</span>
        <Button variant="secondary" onClick={onToggle}>
          {isOpen ? 'Close' : hit.card === 'ask' ? 'Is this you?' : 'Open'}
        </Button>
      </Row>

      {hit.page_url !== null && (
        <p style={{ margin: '8px 0 0' }}>
          <a href={hit.page_url} target="_blank" rel="noopener noreferrer">
            Visit page
          </a>
        </p>
      )}

      {hit.card === 'finding' && severityCopy(hit.severity) !== null && (
        <p style={{ margin: '8px 0 0' }}>
          <strong style={hit.severity === 'ncii_suspected' ? { color: '#FF3B5C' } : undefined}>
            {severityCopy(hit.severity)}
          </strong>
        </p>
      )}

      {isOpen && (
        <div style={{ marginTop: 10 }}>
          {/* The api's own sentence, verbatim. */}
          {hit.question !== null && <p style={{ margin: '0 0 10px' }}>{hit.question}</p>}

          {wanted && (
            <div style={{ margin: '0 0 10px' }}>
              {cropLoading && <p className="empty">loading the picture…</p>}
              {cropUrl !== null && (
                <>
                  {/* THE WHOLE FRAME, not a face crop (services 2026-09-02).
                      Fit it rather than cropping: the context is the point —
                      a person answers "is this you?" by recognising the photo,
                      and object-fit: cover would throw away what they need.
                      maxWidth was 260 when this was a square-ish crop. */}
                  <img
                    src={cropUrl}
                    alt={revealed ? PREVIEW_COPY.altRevealed : PREVIEW_COPY.altBlurred}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 340,
                      width: 'auto',
                      objectFit: 'contain',
                      borderRadius: 8,
                      display: 'block',
                    }}
                  />
                  {!revealed && (
                    <>
                      <p className="muted" style={{ margin: '6px 0' }}>
                        {PREVIEW_COPY.blurredNote}
                      </p>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setRevealed(true);
                        }}
                      >
                        {PREVIEW_COPY.reveal}
                      </Button>
                    </>
                  )}
                  {revealed && (
                    // Says what did NOT happen. Without it, a sharpened face on
                    // a blurred frame reads as a half-loaded image rather than
                    // the deliberate limit it is.
                    <p className="muted" style={{ margin: '6px 0' }}>
                      {PREVIEW_COPY.revealedNote}
                    </p>
                  )}
                </>
              )}
              {cropError !== null && (
                <Failure
                  error={cropError}
                  hint={
                    previewNote(cropError.code) ??
                    'The picture could not be shown. You can still answer.'
                  }
                />
              )}
            </div>
          )}

          {!wanted && hit.card === 'ask' && (
            // preview_expected: false. NOT an error — the ordinary case for a
            // hit nothing has checked, and for one where no face was found.
            <p className="muted" style={{ margin: '0 0 10px' }}>
              {previewNote('PREVIEW_UNAVAILABLE')}
            </p>
          )}

          {hit.answerable ? (
            <>
              <Row>
                {answerLabels().map((option) => (
                  <Button
                    key={option.decision}
                    variant={option.decision === 'confirmed' ? undefined : 'secondary'}
                    disabled={decide.pending}
                    onClick={() => {
                      decide.run(hit.hit_id, option.decision);
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Row>
              <p className="muted">{NOT_SURE_NOTE}</p>
            </>
          ) : (
            <p className="muted">
              {hit.card === 'being_checked'
                ? 'We are still checking this one. There is nothing to answer yet.'
                : 'You have already answered this one.'}
            </p>
          )}

          {decide.error !== null && (
            <Failure error={decide.error} hint={previewNote(decide.error.code) ?? undefined} />
          )}
          {decide.result !== null && <DecisionOutcome result={decide.result} />}
        </div>
      )}
    </div>
  );
}

/**
 * What their answer did. The score line matters: confirming a real match can
 * take the number DOWN, and that is not a penalty — the exposure was always
 * there and the number now tells the truth. Saying so here is the difference
 * between an honest number and one that punishes people for answering.
 */
function DecisionOutcome({ result }: { result: DecisionResult }) {
  return (
    <div style={{ marginTop: 10 }}>
      <p style={{ margin: 0 }}>
        {result.decision === 'confirmed'
          ? 'Recorded — you told us this is you.'
          : 'Recorded — you told us this is not you.'}
        {result.idempotent_replay && (
          <span className="muted"> (you had already answered this; nothing changed)</span>
        )}
      </p>
      <p className="muted" style={{ margin: '4px 0 0' }}>
        {result.decision === 'confirmed'
          ? 'Your protection score may go down. That is the number catching up with what was already there, not a penalty for telling us.'
          : 'This will not count against your protection score.'}
      </p>
      <Raw label="decision response" value={result} />
    </div>
  );
}
